/**
 * Strings come only from @aseed/i18n. Japanese is the reference catalogue; a
 * key missing from another locale falls back to Japanese, never to the key.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import en from "@aseed/i18n/en";
import ja from "@aseed/i18n/ja";
import ko from "@aseed/i18n/ko";
import zhCN from "@aseed/i18n/zh-CN";
import zhTW from "@aseed/i18n/zh-TW";
import { store } from "./storage";

export const LOCALES = [
  { code: "ja", label: "日本語" },
  { code: "en", label: "English" },
  { code: "zh-CN", label: "简体中文" },
  { code: "zh-TW", label: "繁體中文" },
  { code: "ko", label: "한국어" },
] as const;

export type Locale = (typeof LOCALES)[number]["code"];

type Catalog = Readonly<Record<string, unknown>>;

const CATALOGS: Record<Locale, Catalog> = { ja, en, "zh-CN": zhCN, "zh-TW": zhTW, ko };

export function isLocale(value: string | null | undefined): value is Locale {
  return !!value && LOCALES.some((l) => l.code === value);
}

/** The stored choice, else the browser's first supported language, else Japanese. */
export function pickLocale(stored: string | null, languages: readonly string[]): Locale {
  if (isLocale(stored)) return stored;
  for (const lang of languages) {
    const lower = lang.toLowerCase();
    if (lower.startsWith("ja")) return "ja";
    if (lower.startsWith("ko")) return "ko";
    if (lower === "zh-tw" || lower === "zh-hk" || lower.startsWith("zh-hant")) return "zh-TW";
    if (lower.startsWith("zh")) return "zh-CN";
    if (lower.startsWith("en")) return "en";
  }
  return "ja";
}

export type Vars = Record<string, string | number>;

export function translate(locale: Locale, key: string, vars?: Vars): string {
  const raw = CATALOGS[locale][key] ?? ja[key as keyof typeof ja];
  const text = typeof raw === "string" ? raw : key;
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

export function hasKey(key: string): boolean {
  return typeof (ja as Catalog)[key] === "string";
}

interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, vars?: Vars) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() =>
    pickLocale(store.get("locale"), globalThis.navigator?.languages ?? []),
  );
  const setLocale = useCallback((next: Locale) => {
    store.set("locale", next);
    document.documentElement.lang = next;
    setLocaleState(next);
  }, []);
  const value = useMemo<I18n>(
    () => ({ locale, setLocale, t: (key, vars) => translate(locale, key, vars) }),
    [locale, setLocale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n outside I18nProvider");
  return ctx;
}
